import React, { forwardRef, useState, useCallback, useRef } from 'react';
import { useField } from 'formik';
import { Upload, FileText, X } from 'lucide-react';

interface IFileInputField {
  label?: string;
  name: string;
  accept?: string;
  maxSize?: number; // in MB
  onFileSelect?: (file: File | null) => void;
  error?: string;
  touched?: boolean;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  helpText?: string;
  value?: File | null;
  onChange?: (file: File | null) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

// Base File Input Component (Standalone)
const BaseFileInputField = forwardRef<HTMLInputElement, IFileInputField>(
  (
    {
      label,
      name,
      accept = '.pdf,.png,.jpg,.jpeg,.bmp',
      maxSize = 20,
      onFileSelect,
      error = '',
      touched = false,
      required = false,
      disabled = false,
      className = '',
      helpText = `PDF, PNG, JPG, JPEG, or BMP (max. ${maxSize}MB)`,
      value: externalValue,
      onChange: externalOnChange,
      onBlur,
    },
    ref
  ) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(
      externalValue || null
    );
    const [isDragging, setIsDragging] = useState(false);
    const [fileError, setFileError] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState<string>('');

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const validateFile = useCallback(
      (file: File): string | null => {
        // Check file size
        const fileSizeInMB = file.size / (1024 * 1024);
        if (fileSizeInMB > maxSize) {
          return `File size exceeds ${maxSize}MB`;
        }

        // Check file type
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        const acceptedTypes = accept
          .split(',')
          .map((type) => type.trim().toLowerCase());

        if (!acceptedTypes.some((type) => fileExtension === type)) {
          return `Invalid file type. Accepted: ${accept}`;
        }

        return null;
      },
      [accept, maxSize]
    );

    const handleFileSelect = useCallback(
      (file: File) => {
        const validationError = validateFile(file);

        if (validationError) {
          setFileError(validationError);
          setSelectedFile(null);
          setFilePreviewUrl('');
          onFileSelect?.(null);
          externalOnChange?.(null);
          return;
        }

        setFileError('');
        setSelectedFile(file);

        const url = URL.createObjectURL(file);
        setFilePreviewUrl(url);

        onFileSelect?.(file);
        externalOnChange?.(file);
      },
      [validateFile, onFileSelect, externalOnChange]
    );

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    };

    const handleClick = () => {
      if (!disabled) {
        fileInputRef.current?.click();
      }
    };

    const handleRemoveFile = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedFile(null);
      setFileError('');
      setFilePreviewUrl('');
      onFileSelect?.(null);
      externalOnChange?.(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const handleViewFile = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (filePreviewUrl) {
        window.open(filePreviewUrl, '_blank');
      }
    };

    const displayError = fileError || (touched && error);

    return (
      <div className={className}>
        {label && (
          <div className="flex items-center mb-1 gap-1">
            <label
              htmlFor={name}
              className="font-medium text-base text-black"
            >
              {label}
            </label>
            {required && (
              <span className="text-red-500 text-sm font-medium">*</span>
            )}
          </div>
        )}

        <div
          className={`
            relative border-2 rounded-lg transition-all
            ${
              isDragging
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300 bg-white'
            }
            ${
              disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer hover:border-blue-300'
            }
            ${displayError ? 'border-red-500' : ''}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            name={name}
            accept={accept}
            onChange={handleFileChange}
            onBlur={onBlur}
            disabled={disabled}
            className="hidden"
          />

          {!selectedFile ? (
            // Empty state - upload prompt
            <div className="py-4 text-center">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <Upload className="w-6 h-6 text-gray-400" />
                </div>
              </div>
              <div className="mb-2">
                <span className="text-blue-600 font-medium hover:underline">
                  Click to upload
                </span>
                <span className="text-gray-600"> or drag and drop</span>
              </div>
              <p className="text-sm text-gray-500">{helpText}</p>
            </div>
          ) : (
            // File selected state
            <div className="py-4 px-2 md:px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate max-w-[20ch] md:max-w-none">
                    {selectedFile.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="p-1 rounded hover:bg-gray-100 transition-colors flex-shrink-0"
                  aria-label="Remove file"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <button
                type="button"
                onClick={handleViewFile}
                className="w-full py-2.5 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors"
              >
                View file
              </button>
            </div>
          )}
        </div>

        {displayError && (
          <p
            className="text-red-500 text-sm mt-1"
            id={`${name}-error`}
          >
            {displayError}
          </p>
        )}
      </div>
    );
  }
);

BaseFileInputField.displayName = 'BaseFileInputField';

// Formik-compatible File Input Component
type FormikFileInputFieldProps = Omit<
  IFileInputField,
  'value' | 'onChange' | 'error' | 'touched'
>;

const FormikFileInputField = forwardRef<
  HTMLInputElement,
  FormikFileInputFieldProps
>((props, ref) => {
  const { name, ...rest } = props;
  const [{ value }, { touched, error }, { setValue, setTouched }] =
    useField<File | null>(name);

  const handleFileChange = useCallback(
    (file: File | null) => {
      setValue(file, true);
    },
    [setValue]
  );

  const handleBlur = useCallback(() => {
    setTouched(true, true);
  }, [setTouched]);

  return (
    <BaseFileInputField
      ref={ref}
      name={name}
      value={value}
      onChange={handleFileChange}
      onBlur={handleBlur}
      error={error}
      touched={touched}
      {...rest}
    />
  );
});

FormikFileInputField.displayName = 'FormikFileInputField';

// Export both versions
export { BaseFileInputField as FileInputField, FormikFileInputField };
export default FormikFileInputField;
